import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ShoppingCart } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import { SELF_PICKUP } from '@/lib/pickupHours'
import { useShippingInfo } from '@/lib/addresses'
import type { CustomerRow, ItemSpec, OptionSets, Paginated, PersonalizationRow, Product } from '@/types'
import ProductImage from '@/components/order/ProductImage'
import Card, { SectionLabel } from '@/components/ui/Card'
import PageHeader from '@/components/ui/PageHeader'
import Button from '@/components/ui/Button'
import Badge from '@/components/ui/Badge'
import { Field, Input, RequiredMark } from '@/components/ui/Field'
import Modal from '@/components/ui/Modal'
import { Select } from '@/components/ui/Select'
import SpecFields from '@/components/order/SpecFields'
import { useToast } from '@/components/ui/toast'
import LogoLoader from '@/components/ui/LogoLoader'
import { cn } from '@/lib/cn'
import { useAuth } from '@/context/AuthContext'
import { money } from '@/lib/money'
import { incompleteRows, syncPersonalizations } from '@/lib/personalization'
import { groupList, missingRequired, requiredKeys, useOptionGroups } from '@/lib/options'
import ArtworkPicker, { type Artwork } from '@/components/order/ArtworkPicker'
import { artworkPayload, fontPayload } from '@/lib/artwork'
import { ordersListPath } from '@/lib/roles'
import DeliveryDatePicker from '@/components/order/DeliveryDatePicker'
import { urgentRateOn, useDeliveryQuote } from '@/lib/delivery'

interface TeamMemberRow {
  id: number
  name: string
  role: 'admin' | 'staff' | 'sales'
  status: 'approved' | 'suspended'
}


interface DraftItem {
  product: Product
  sizeMatrix: Record<string, number>
  spec: ItemSpec
  /** Yes/No name & number choice; rows are one per piece and all compulsory when on. */
  wantsNames: boolean
  personalizations: PersonalizationRow[]
  /** The print files in order (front, back, ...), uploaded before the order is placed. */
  artworks: Artwork[]
  /** The font behind the names. Optional here; checkout is where it is required. */
  fonts: Artwork[]
  /** The total agreed with the customer for this whole line, as typed; blank charges the normal price. */
  agreedTotal: string
}

/**
 * The team's own price for one line, typed as the line's total because that
 * is the figure actually agreed (client request 2026-10-14). Blank means the
 * system prices it as usual (tier or agreed customer price, plus add-ons and
 * rush); a figure here replaces all of that, which is how a long sleeve still
 * goes at RM35. The server charges the total exactly, even where it does not
 * divide into whole sen a piece.
 */
function AgreedTotalField({ value, pieces, onChange }: { value: string; pieces: number; onChange: (value: string) => void }) {
  const total = Number(value)
  const set = value.trim() !== '' && total > 0

  return (
    <div className="mt-3 border-t border-line pt-3">
      <Field label="Agreed total for this item (optional)">
        <div className="grid gap-2 sm:flex sm:items-center sm:gap-3">
          <Input
            type="number"
            inputMode="decimal"
            min={0.01}
            step="0.01"
            value={value}
            placeholder="Normal price"
            onChange={(e) => onChange(e.target.value)}
            className="w-full sm:w-40"
          />
          <p className="text-xs text-muted">
            {set && pieces > 0
              ? `${money(total)} for ${pieces} pcs, about ${money(total / pieces)} / pc. Replaces the normal price, add-ons and rush included.`
              : set
                ? `${money(total)} for the whole item. Replaces the normal price, add-ons and rush included.`
                : 'The total for all the pieces of this item. Leave blank to charge the normal price for this customer.'}
          </p>
        </div>
      </Field>
    </div>
  )
}

/**
 * The manual order form, shared by the admin and sales consoles.
 *
 * Three capabilities matter and are passed in rather than inferred:
 *
 * - canChoosePic. Sales is always its own PIC, so the picker does not render
 *   AND the /admin/users query is never issued - that response carries every
 *   staff and admin email, role and department.
 * - canBypassQuota. Opting out of the quota skips the daily capacity check and
 *   the lock behind it. The sales endpoint never reads the field, so this only
 *   controls whether the checkbox is offered.
 * - canOrderOnCredit. An unpaid order is produced before any money arrives,
 *   an admin decision; the sales endpoint refuses it.
 * - canAgreePrice. A total typed per line, all-in (add-ons and rush
 *   included), for the in-house deals the client asked for on 2026-10-05.
 *   Per line rather than per piece since 2026-10-14.
 *   Both consoles have it today; it is a flag so one can lose it alone.
 */
export default function CreateOrderPage({
  basePath,
  canChoosePic,
  canBypassQuota,
  canOrderOnCredit,
  canAgreePrice,
}: {
  basePath: '/admin' | '/sales'
  canChoosePic: boolean
  canBypassQuota: boolean
  canOrderOnCredit: boolean
  canAgreePrice: boolean
}) {
  const toast = useToast()
  const navigate = useNavigate()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [agentId, setAgentId] = useState('')
  const [items, setItems] = useState<DraftItem[]>([])
  // Placed by delivery date, like checkout (client request 2026-10-06):
  // production starts on the first day with room, and the picked date
  // decides urgent or normal.
  const [deliveryDate, setDeliveryDate] = useState<string | null>(null)
  const [deliveryMethod, setDeliveryMethod] = useState('')
  const [salesPerson, setSalesPerson] = useState('')
  // wallet charges the customer now, offline records money taken at the
  // counter, unpaid produces the order on credit to be marked paid later.
  const [payment, setPayment] = useState<'wallet' | 'offline' | 'unpaid'>('offline')
  const [paymentReference, setPaymentReference] = useState('')
  const [useQuota, setUseQuota] = useState(false)
  // Sales orders always book the quota - the sales endpoint hardcodes it - so
  // the calendar has to grey out full days there rather than offering a date
  // the server will refuse.
  const countsQuota = canBypassQuota ? useQuota : true
  const [notes, setNotes] = useState('')
  // The job sheet's header fields. Optional: a storefront order has neither,
  // and the sheet derives a title from the items when the name is blank.
  const [jobName, setJobName] = useState('')
  const [customerRef, setCustomerRef] = useState('')

  // Server-side customer search: the endpoint paginates at 15, so without a
  // search only the 15 newest customers would ever be reachable here.
  const [customerSearch, setCustomerSearch] = useState('')
  const [customerNeedle, setCustomerNeedle] = useState('')
  useEffect(() => {
    const handle = setTimeout(() => setCustomerNeedle(customerSearch), 300)
    return () => clearTimeout(handle)
  }, [customerSearch])
  // The picked customer must stay renderable after a later search drops them
  // from the results page.
  const [selectedAgent, setSelectedAgent] = useState<{ value: string; label: string } | null>(null)
  // The picked customer's tier, which the option labels price add-ons at
  // (per tier since 2026-10-17). A walk-in added here has none yet.
  const [agentTierId, setAgentTierId] = useState<number | null>(null)
  const [newCustomer, setNewCustomer] = useState<{ name: string; phone: string; company_name: string } | null>(null)

  const agents = useQuery({
    queryKey: ['customers-page', basePath, 'picker', customerNeedle],
    queryFn: async () =>
      (
        await api.get<Paginated<CustomerRow>>(`${basePath}/customers`, {
          params: { status: 'approved', ...(customerNeedle ? { search: customerNeedle } : {}) },
        })
      ).data.data,
    placeholderData: keepPreviousData,
  })

  const team = useQuery({
    queryKey: ['admin-users'],
    queryFn: async () => (await api.get<{ data: TeamMemberRow[] }>('/admin/users')).data.data,
    // Sales is always its own PIC, so this never runs there - and it must
    // not, because the response lists every staff and admin email.
    enabled: canChoosePic,
  })
  const shippingInfo = useShippingInfo()
  const products = useQuery({
    queryKey: ['products-picker', basePath],
    queryFn: async () => (await api.get<{ data: Product[] }>(`${basePath}/products`)).data.data,
  })
  const optionSets = useQuery({
    queryKey: ['options'],
    queryFn: async () => (await api.get<{ data: OptionSets }>('/options')).data.data,
  })
  const optionGroups = useOptionGroups()

  const totalPieces = useMemo(
    () => items.reduce((sum, item) => sum + Object.values(item.sizeMatrix).reduce((a, b) => a + b, 0), 0),
    [items],
  )

  // Each product's pieces across its lines, which pick its rush rate.
  const productPieces = (productId: number) =>
    items
      .filter((item) => item.product.id === productId)
      .reduce((sum, item) => sum + Object.values(item.sizeMatrix).reduce((a, b) => a + b, 0), 0)
  const deliveryQuote = useDeliveryQuote(
    [...new Set(items.map((item) => item.product.id))].map((productId) => ({ productId, pieces: productPieces(productId) })),
    countsQuota,
  )
  // The rush a date adds on top of the normal price, at that date's own rate
  // per product ("makin cepat makin mahal"). Lines with an agreed total pay
  // that instead, all-in.
  const rushOn = (date: string | null) =>
    items.reduce((sum, item) => {
      if (item.agreedTotal.trim()) return sum
      const pcs = Object.values(item.sizeMatrix).reduce((a, b) => a + b, 0)
      return sum + pcs * urgentRateOn(deliveryQuote.data, date, item.product.id)
    }, 0)
  const rushDelta = rushOn(deliveryDate)

  const toggleProduct = (product: Product) => {
    setItems((list) => {
      const existing = list.find((i) => i.product.id === product.id)
      if (existing) return list.filter((i) => i.product.id !== product.id)
      return [...list, { product, sizeMatrix: {}, spec: {}, wantsNames: false, personalizations: [], artworks: [], fonts: [], agreedTotal: '' }]
    })
  }

  const patchItem = (productId: number, patch: Partial<DraftItem>) => {
    setItems((list) => list.map((item) => (item.product.id === productId ? { ...item, ...patch } : item)))
  }

  // Same inline-create idea as "Add category" on the product form: the
  // walk-in customer is created without leaving the order.
  const addCustomer = useMutation({
    mutationFn: async (d: { name: string; phone: string; company_name: string }) =>
      (
        await api.post<{ message?: string; customer: CustomerRow }>(`${basePath}/customers`, {
          name: d.name,
          phone: d.phone,
          company_name: d.company_name || null,
        })
      ).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['customers-page'] })
      const label = `${res.customer.name}${res.customer.phone ? ` (${res.customer.phone})` : ''}`
      setAgentId(String(res.customer.id))
      setSelectedAgent({ value: String(res.customer.id), label })
      setAgentTierId(res.customer.tier?.id ?? null)
      setNewCustomer(null)
      toast.success(res.message ?? 'Customer added')
    },
    onError: (err) => toast.error('Could not add the customer', errorMessage(err)),
  })

  const create = useMutation({
    mutationFn: async () =>
      (
        await api.post(`${basePath}/orders`, {
          agent_id: Number(agentId),
          sales_user_id: Number(picId),
          payment,
          payment_reference: payment === 'offline' ? paymentReference.trim() || null : null,
          use_quota: countsQuota,
          delivery_date: deliveryDate,
          delivery_method: deliveryMethod,
          notes: notes || null,
          job_name: jobName || null,
          customer_ref: customerRef || null,
          items: items.map((item) => ({
            product_id: item.product.id,
            size_matrix: item.sizeMatrix,
            spec: item.spec,
            personalizations: item.personalizations,
            ...artworkPayload(item.artworks),
            // Only a line with names carries its font; switching names off
            // leaves the upload behind rather than on the order.
            ...(item.wantsNames && item.fonts.length > 0 ? fontPayload(item.fonts) : {}),
            ...(canAgreePrice && item.agreedTotal.trim() ? { agreed_total: Number(item.agreedTotal) } : {}),
          })),
        })
      ).data,
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['orders-page'] })
      queryClient.invalidateQueries({ queryKey: ['admin-dash'] })
      toast.success(res.message ?? 'Order created')
      navigate(ordersListPath(basePath))
    },
    onError: (err) => toast.error('Create failed', errorMessage(err)),
  })

  // Not gated on the customer picker: its query re-runs on every search.
  if (products.isLoading) return <LogoLoader />

  const customerOptions = (agents.data ?? []).map((a) => ({
    value: String(a.id),
    label: `${a.name}${a.company_name ? ` (${a.company_name})` : a.phone ? ` (${a.phone})` : ''}`,
  }))
  if (selectedAgent && !customerOptions.some((option) => option.value === selectedAgent.value)) {
    customerOptions.unshift(selectedAgent)
  }

  const picOptions = (team.data ?? [])
    .filter((member) => member.status === 'approved' && (member.role === 'sales' || member.role === 'admin'))
    .map((member) => ({ value: String(member.id), label: member.name }))

  // Sales cannot name anyone else, and the server ignores the field on that
  // path anyway - this only keeps the form's own ready check honest.
  const picId = canChoosePic ? salesPerson : String(user?.id ?? '')

  // Couriers come from the admin's rate cards; the option set only fills in
  // while shipping info is still loading. Same fallback as checkout.
  const courierOptions =
    shippingInfo.data?.couriers ??
    (optionSets.data?.delivery_methods ?? []).filter((method) => method !== SELF_PICKUP)

  // Compulsory options left empty, one line per item, so the disabled Create
  // button says why instead of looking dead. The server refuses the same.
  const missingOptions = items.flatMap((item) => {
    const missing = missingRequired(item.spec, requiredKeys(item.product.options), optionGroups.data)
    return missing.length > 0 ? [`Choose a ${groupList(missing)} for ${item.product.name}.`] : []
  })
  // Same rule as the server (min 0.01): blank is fine, zero or junk is not.
  const badPrices = items.flatMap((item) =>
    item.agreedTotal.trim() && !(Number(item.agreedTotal) > 0) ? [`Enter a total above 0 for ${item.product.name}, or leave it blank.`] : [],
  )

  const ready =
    missingOptions.length === 0 &&
    badPrices.length === 0 &&
    agentId &&
    items.length > 0 &&
    totalPieces > 0 &&
    deliveryDate &&
    deliveryMethod &&
    picId &&
    items.every((item) => !item.wantsNames || incompleteRows(item.personalizations).length === 0)

  return (
    <div>
      <PageHeader
        title="Create custom order"
        subtitle="For walk-in / WhatsApp orders taken by a sales person on behalf of a customer"
      />

      <div className="space-y-4">
        <Card>
          <SectionLabel>Order info</SectionLabel>
          <div className="grid gap-3 sm:grid-cols-3">
            <Field label="Customer" required>
              <Select
                value={agentId}
                onChange={(v) => {
                  setAgentId(v)
                  setSelectedAgent(customerOptions.find((option) => option.value === v) ?? null)
                  setAgentTierId((agents.data ?? []).find((a) => String(a.id) === v)?.tier?.id ?? null)
                }}
                placeholder="Select customer..."
                searchable
                onSearch={setCustomerSearch}
                searchPlaceholder="Search name, phone, company..."
                options={customerOptions}
                action={{ label: 'Add customer', onSelect: () => setNewCustomer({ name: '', phone: '', company_name: '' }) }}
              />
            </Field>
            <Field label="Sales person (PIC)" required={canChoosePic}>
              {canChoosePic ? (
                <Select
                  value={salesPerson}
                  onChange={setSalesPerson}
                  placeholder="Select PIC..."
                  options={picOptions}
                />
              ) : (
                <p className="py-2 text-sm font-semibold">{user?.name}</p>
              )}
            </Field>
            <Field label="Delivery method" required>
              <Select
                value={deliveryMethod}
                onChange={setDeliveryMethod}
                placeholder="Select..."
                options={[SELF_PICKUP, ...courierOptions].map((m) => ({ value: m, label: m }))}
              />
            </Field>
            {/* Both print on the job sheet: the name is what the floor calls
                this job, the ref is what the customer calls it. */}
            <Field label="Job name (optional)" className="sm:col-span-2">
              <Input
                value={jobName}
                onChange={(e) => setJobName(e.target.value)}
                maxLength={150}
                placeholder="e.g. CUSTOM WPRS KUNING NO WORDING TAMBAH NAMA - 7PCS"
              />
            </Field>
            <Field label="Customer order ref (optional)">
              <Input
                value={customerRef}
                onChange={(e) => setCustomerRef(e.target.value)}
                maxLength={100}
                placeholder="Customer's own reference"
              />
            </Field>
          </div>
          <fieldset className="mt-3">
            <legend className="text-sm text-foreground">
              Payment
              <RequiredMark />
            </legend>
            <div className="mt-1.5 flex flex-wrap gap-x-5 gap-y-2">
              {(
                [
                  { value: 'offline', label: 'Paid offline (cash or transfer)' },
                  { value: 'wallet', label: "Deduct from the customer's wallet" },
                  ...(canOrderOnCredit ? [{ value: 'unpaid', label: 'On credit - pay later' }] : []),
                ] as { value: typeof payment; label: string }[]
              ).map((choice) => (
                <label key={choice.value} className="flex items-center gap-2 text-sm font-medium">
                  <input
                    type="radio"
                    name="payment"
                    checked={payment === choice.value}
                    onChange={() => setPayment(choice.value)}
                    className="size-4 accent-primary"
                  />
                  {choice.label}
                </label>
              ))}
            </div>
            {payment === 'offline' && (
              <Input
                value={paymentReference}
                onChange={(e) => setPaymentReference(e.target.value)}
                maxLength={100}
                placeholder="Receipt or transfer reference (optional)"
                className="mt-2 sm:w-80"
              />
            )}
            {payment === 'unpaid' && (
              <p className="mt-1.5 text-xs text-muted">
                Produced straight away. Record the payment on the order page once the money arrives.
              </p>
            )}
          </fieldset>

        </Card>

        <Card>
          <SectionLabel>
            Products
            <RequiredMark />
          </SectionLabel>
          <div className="grid gap-3 sm:grid-cols-3">
            {products.data?.filter((p) => p.active).map((product) => {
              const selected = items.some((i) => i.product.id === product.id)
              return (
                <button
                  key={product.id}
                  onClick={() => toggleProduct(product)}
                  className={cn(
                    'flex items-center gap-3 rounded-card border p-3 text-left transition-colors',
                    selected ? 'border-primary bg-primary/10' : 'border-line bg-surface hover:border-primary/40',
                  )}
                >
                  <ProductImage product={product} className="size-12 shrink-0" />
                  <span>
                    <p className="text-sm font-bold">{product.name}</p>
                    <p className="mt-0.5 text-xs text-muted">{product.category}</p>
                  </span>
                </button>
              )
            })}
          </div>

          {items.map((item) => (
            <div key={item.product.id} className="mt-4 rounded-lg border border-line p-4">
              <div className="flex items-center justify-between">
                <p className="font-bold">{item.product.name}</p>
                <Badge tone="brand">{Object.values(item.sizeMatrix).reduce((a, b) => a + b, 0)} pcs</Badge>
              </div>
              <p className="mt-3 text-base font-bold">
                Size quantities
                <RequiredMark />
              </p>
              <div className="mt-2 grid grid-cols-4 gap-2 sm:grid-cols-7">
                {item.product.sizes.map((size) => (
                  <Field key={size} label={size}>
                    <Input
                      type="number"
                      min={0}
                      value={item.sizeMatrix[size] ?? ''}
                      placeholder="0"
                      onChange={(e) => {
                        const sizeMatrix = { ...item.sizeMatrix, [size]: Math.max(0, Number(e.target.value)) }
                        patchItem(item.product.id, {
                          sizeMatrix,
                          ...(item.wantsNames
                            ? { personalizations: syncPersonalizations(item.personalizations, sizeMatrix, item.product.sizes) }
                            : {}),
                        })
                      }}
                    />
                  </Field>
                ))}
              </div>
              <SpecFields
                product={item.product}
                spec={item.spec}
                onSpec={(spec) => patchItem(item.product.id, { spec })}
                personalizations={item.personalizations}
                onPersonalizations={(personalizations) => patchItem(item.product.id, { personalizations })}
                personalizationOn={item.wantsNames}
                onPersonalizationToggle={(on) =>
                  patchItem(item.product.id, {
                    wantsNames: on,
                    personalizations: on ? syncPersonalizations(item.personalizations, item.sizeMatrix, item.product.sizes) : [],
                  })
                }
                onImport={({ sizeMatrix, rows }) => patchItem(item.product.id, { sizeMatrix, personalizations: rows })}
                tierId={agentTierId}
              />
              <div className="mt-3 border-t border-line pt-3">
                <p className="text-base font-bold">Artwork</p>
                <ArtworkPicker
                  variant="dropzone"
                  value={item.artworks}
                  basePath={basePath}
                  onChange={(artworks) => patchItem(item.product.id, { artworks })}
                />
              </div>
              {item.wantsNames && (
                <div className="mt-3 border-t border-line pt-3">
                  <p className="text-base font-bold">
                    Font file <span className="text-xs font-normal text-muted">(optional)</span>
                  </p>
                  <p className="text-xs text-muted">The font for the names and numbers, if the customer sent one.</p>
                  <ArtworkPicker
                    kind="font"
                    variant="dropzone"
                    preview={false}
                    value={item.fonts}
                    basePath={basePath}
                    onChange={(fonts) => patchItem(item.product.id, { fonts })}
                  />
                </div>
              )}
              {canAgreePrice && (
                <AgreedTotalField
                  value={item.agreedTotal}
                  pieces={Object.values(item.sizeMatrix).reduce((a, b) => a + b, 0)}
                  onChange={(agreedTotal) => patchItem(item.product.id, { agreedTotal })}
                />
              )}
            </div>
          ))}
        </Card>

        <Card>
          <SectionLabel>
            Delivery date
            <RequiredMark /> - {totalPieces} pcs
          </SectionLabel>
          {canBypassQuota && (
            <label className="mb-3 flex items-center gap-2 text-sm font-medium">
              <input
                type="checkbox"
                checked={useQuota}
                onChange={(e) => setUseQuota(e.target.checked)}
                className="size-4 accent-primary"
              />
              Use production quota (off = this order does not block or count against the day's pcs limit)
            </label>
          )}
          {items.length === 0 ? (
            <p className="text-sm text-muted">Add a product first; the dates depend on what is being made.</p>
          ) : (
            <DeliveryDatePicker
              quote={deliveryQuote.data}
              loading={deliveryQuote.isLoading}
              value={deliveryDate}
              onChange={setDeliveryDate}
              pieces={totalPieces}
              countsQuota={countsQuota}
              variant="portal"
              urgentNote={rushDelta > 0 ? `+${money(rushDelta)}` : 'at each product\'s rush rate'}
              rushFor={(date) => {
                const total = rushOn(date)
                return total > 0 ? `+${money(total)}` : null
              }}
            />
          )}
          <Field label="Notes (optional)" className="mt-4">
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
          </Field>
        </Card>

        <div className="flex flex-col items-end gap-2">
          {[...missingOptions, ...badPrices].map((message) => (
            <p key={message} className="text-right text-xs font-semibold text-warning">
              {message}
            </p>
          ))}
          <Button disabled={!ready} loading={create.isPending} onClick={() => create.mutate()}>
            <ShoppingCart className="size-4" /> Create order
          </Button>
        </div>
      </div>

      <Modal
        open={Boolean(newCustomer)}
        onClose={() => setNewCustomer(null)}
        title="Add customer"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setNewCustomer(null)}>
              Cancel
            </Button>
            <Button
              loading={addCustomer.isPending}
              disabled={!newCustomer?.name.trim() || !newCustomer?.phone.trim()}
              onClick={() => newCustomer && addCustomer.mutate(newCustomer)}
            >
              Add customer
            </Button>
          </div>
        }
      >
        {newCustomer && (
          <div className="space-y-4">
            <Field label="Full name" htmlFor="new-customer-name" required>
              <Input
                id="new-customer-name"
                value={newCustomer.name}
                onChange={(e) => setNewCustomer({ ...newCustomer, name: e.target.value })}
              />
            </Field>
            <Field label="Phone" htmlFor="new-customer-phone" required>
              <Input
                id="new-customer-phone"
                value={newCustomer.phone}
                onChange={(e) => setNewCustomer({ ...newCustomer, phone: e.target.value })}
              />
            </Field>
            <Field label="Company (optional)" htmlFor="new-customer-company">
              <Input
                id="new-customer-company"
                value={newCustomer.company_name}
                onChange={(e) => setNewCustomer({ ...newCustomer, company_name: e.target.value })}
              />
            </Field>
            <p className="text-xs text-foreground/60">
              Walk-in customer without an account: they cannot sign in, but the order can be created for them
              right away. Name and phone are enough.
            </p>
          </div>
        )}
      </Modal>
    </div>
  )
}
